// Endpoint functions — one per backend route, typed. Components/hooks call
// these, never `fetch` directly.

import type {
  AgentBehaviour,
  AgentBehaviourPrinciples,
  AgentBehaviourVersion,
  TenantMap,
  AgentStatus,
  AgentView,
  AttachmentView,
  GenerationParams,
  Member,
  MemberCredential,
  MemberRole,
  ObservabilitySettings,
  ObservabilityTestResult,
  CapabilityView,
  DeliveryMode,
  DomainView,
  EmbeddingModelView,
  ExecutionType,
  FindingStatus,
  GithubInstallation,
  GovernanceActivityView,
  GovernanceFindingView,
  GovernanceSettings,
  GovernanceSummary,
  GovernanceVerdict,
  GovernanceSeverity,
  RepoCheck,
  RepoScanView,
  ScanKind,
  SecurityFindingStatus,
  SecurityAiReview,
  SecurityFindingView,
  SecuritySummary,
  GovernedFileView,
  GovernedRepoView,
  PrReviewDetail,
  PrReviewView,
  ReviewStatus,
  FailureBehavior,
  KbAttachmentView,
  KbCheckConnectionResult,
  KbQueryResult,
  KnowledgeBaseView,
  RiskTier,
  LlmModelView,
  ModelCatalog,
  ModelInput,
  ModelTestResult,
  AuditRunDetail,
  AuditRunPage,
  EmbeddingUsagePage,
  LlmUsagePage,
  GovernanceAuditPage,
  SemanticCacheSettings,
  SemanticCacheUpdate,
  VectorStoreInput,
  VectorStoreUpdateInput,
  VectorStoreView,
  GoogleSsoConfig,
  LoginResult,
  OrganizationChoice,
  Me,
  MessageResponse,
  ModelPricing,
  MyTenant,
  PlatformEmbeddingModel,
  PlatformEmbeddingModelInput,
  PlatformEmbeddingModelUpdate,
  PlatformLlmModel,
  PlatformLlmModelInput,
  PlatformLlmModelUpdate,
  PlatformModelKind,
  ProvisionTenantResult,
  RunRow,
  SkillView,
  SsoPending,
  SsoPublicConfig,
  Tenant,
  TenantAdminCredential,
  TrustedSystem,
  TrustedSystemCheckResult,
  UpdateGoogleSsoConfig,
  ValidatorAttachment,
  ValidatorCatalog,
  LiveVersion,
  VersionDiff,
  VersionLlm,
  VersionLlmHealth,
  VersionView,
} from "@/types/api";
import { ApiError, apiFetch } from "./client";

// ---- auth / session -------------------------------------------------------

export const getMe = () => apiFetch<Me>("/admin/me");

export const superadminLogin = (email: string, password: string) =>
  apiFetch<LoginResult>("/admin/superadmin/login", {
    method: "POST",
    body: { email, password },
  });

/** Tenant-admin sign-in. Omit `tenant_slug` to let the server resolve the
 *  organisation from the credentials — it signs in when exactly one matches and
 *  returns `choices` when several do. */
export const tenantAdminLogin = (email: string, password: string, tenant_slug?: string) =>
  apiFetch<LoginResult>("/admin/login", {
    method: "POST",
    body: tenant_slug ? { tenant_slug, email, password } : { email, password },
  });

/** Organisations the current session may switch between. */
export const listMyOrganizations = () => apiFetch<OrganizationChoice[]>("/admin/me/organizations");

/** Re-bind the session to another of the caller's organisations. */
export const switchOrganization = (tenant_slug: string) =>
  apiFetch<Me>("/admin/session/switch", {
    method: "POST",
    body: { tenant_slug },
  });

/** Whether an organisation's login page offers "Login with Google". */
export const getSsoPublicConfig = (slug: string) =>
  apiFetch<SsoPublicConfig>(`/admin/sso/config/${encodeURIComponent(slug)}`);

/** Browser URL that starts Google sign-in with the organisation's own client.
 *  A full-page navigation, not a fetch: it redirects to Google. */
export const googleSsoStartUrl = (slug: string) =>
  `/api/admin/sso/google/start?org=${encodeURIComponent(slug)}`;

export const getSsoPending = () => apiFetch<SsoPending>("/admin/sso/pending");

export const selectSsoOrganization = (tenant_slug: string) =>
  apiFetch<LoginResult>("/admin/sso/pending/select", {
    method: "POST",
    body: { tenant_slug },
  });

export const changePassword = (current_password: string, new_password: string) =>
  apiFetch<MessageResponse>("/admin/change-password", {
    method: "POST",
    body: { current_password, new_password },
  });

export const logout = () => apiFetch<MessageResponse>("/admin/logout", { method: "POST" });

// ---- superadmin: tenants --------------------------------------------------

export const listTenants = () => apiFetch<Tenant[]>("/admin/tenants");

export const getTenant = (slug: string) => apiFetch<Tenant>(`/admin/tenants/${slug}`);

export const createTenant = (input: {
  slug: string;
  display_name: string;
  admin_email: string;
  region?: string | null;
}) => apiFetch<ProvisionTenantResult>("/admin/tenants", { method: "POST", body: input });

export const suspendTenant = (slug: string) =>
  apiFetch<Tenant>(`/admin/tenants/${slug}/suspend`, { method: "POST" });

export const activateTenant = (slug: string) =>
  apiFetch<Tenant>(`/admin/tenants/${slug}/activate`, { method: "POST" });

export const getTenantGoogleSso = (slug: string) =>
  apiFetch<GoogleSsoConfig>(`/admin/tenants/${slug}/sso/google`);

export const updateTenantGoogleSso = (slug: string, body: UpdateGoogleSsoConfig) =>
  apiFetch<GoogleSsoConfig>(`/admin/tenants/${slug}/sso/google`, { method: "PUT", body });

export const issueTenantAdmin = (slug: string, email: string) =>
  apiFetch<TenantAdminCredential>(`/admin/tenants/${slug}/admins`, {
    method: "POST",
    body: { email },
  });

// ---- tenant-admin: trusted systems ---------------------------------------
// The registry mapping an inbound JWT's `iss` claim to the JWKS Aviary
// verifies against. Tenant-owned since ADR-021: a row carries its tenant, and
// auth reads the caller's tenant off the row rather than off the token, so a
// tenant's registration can only ever produce that tenant's identities. That
// is what makes self-service onboarding safe — no operator in the loop.

export const listTrustedSystems = () => apiFetch<TrustedSystem[]>("/admin/my/trusted-systems");

export const createTrustedSystem = (input: {
  identifier: string;
  name: string;
  issuer?: string | null;
  jwks_url?: string | null;
  audience: string;
}) => apiFetch<TrustedSystem>("/admin/my/trusted-systems", { method: "POST", body: input });

// Item routes address rows by numeric id, not by identifier. An OIDC issuer is
// a URL, and `https://dev.myfam.app` cannot survive a path segment — encoded or
// not, the slashes make the route unmatchable.
export const updateTrustedSystem = (
  id: number,
  input: { name?: string; issuer?: string | null; jwks_url?: string | null; audience?: string },
) =>
  apiFetch<TrustedSystem>(`/admin/my/trusted-systems/${id}`, {
    method: "PATCH",
    body: input,
  });

export const deleteTrustedSystem = (id: number) =>
  apiFetch<void>(`/admin/my/trusted-systems/${id}`, { method: "DELETE" });

export const checkTrustedSystem = (id: number) =>
  apiFetch<TrustedSystemCheckResult>(`/admin/my/trusted-systems/${id}/check`, {
    method: "POST",
  });

// ---- tenant-admin: self ---------------------------------------------------

export const getMyTenant = () => apiFetch<MyTenant>("/admin/my/tenant");

/** What this tenant needs in order to grant Aviary read access to their bucket.
 *
 *  Every field is a fact about *our* side of the cross-account trust, so none of
 *  it is derivable inside the customer's account (ADR-019). `external_id` is
 *  generated per tenant and is deliberately shown rather than collected: the
 *  customer pastes it into their role's trust policy, and it is unguessable by
 *  any other tenant, which is the whole confused-deputy defence. */
export interface KbAwsOnboarding {
  external_id: string;
  /** Both of our roles — the API one tests the connection, the job one ingests.
   *  A trust policy naming only the first passes Test and fails at ingest.
   *  Empty when the deployment has configured none. */
  principal_arns: string[];
  role_path: string;
  template_url: string | null;
}

export const getKbAwsOnboarding = () => apiFetch<KbAwsOnboarding>("/admin/my/kb-aws-onboarding");

// ---- tenant-admin authoring: domains --------------------------------------

export const listMyDomains = () => apiFetch<DomainView[]>("/admin/my/domains");

// Shared write shape. Origin/data fields are optional; sending `allowed_issuers: []`
// explicitly clears a restriction, while omitting the field leaves it unchanged.
export interface DomainWrite {
  name?: string;
  slug?: string;
  description?: string | null;
  knowledge_source?: string | null;
  vector_index_namespace?: string | null;
  allowed_issuers?: string[];
  allowed_audiences?: string[];
}

export const createDomain = (input: DomainWrite & { name: string }) =>
  apiFetch<DomainView>("/admin/my/domains", { method: "POST", body: input });

export const updateDomain = (slug: string, input: DomainWrite) =>
  apiFetch<DomainView>(`/admin/my/domains/${slug}`, { method: "PATCH", body: input });

export const deleteDomain = (slug: string) =>
  apiFetch<void>(`/admin/my/domains/${slug}`, { method: "DELETE" });

// ---- tenant-admin authoring: agents ---------------------------------------

export const listMyAgents = () => apiFetch<AgentView[]>("/admin/my/agents");

export const createAgent = (input: {
  name: string;
  domain_slug: string;
  slug?: string;
  description?: string | null;
}) => apiFetch<AgentView>("/admin/my/agents", { method: "POST", body: input });

export const updateAgent = (
  slug: string,
  input: { name?: string; domain_slug?: string; description?: string | null },
) => apiFetch<AgentView>(`/admin/my/agents/${slug}`, { method: "PATCH", body: input });

export const deleteAgent = (slug: string) =>
  apiFetch<void>(`/admin/my/agents/${slug}`, { method: "DELETE" });

export const getAgent = (slug: string) => apiFetch<AgentView>(`/admin/my/agents/${slug}`);

// ---- tenant-admin authoring: versions -------------------------------------

const vBase = (agentSlug: string) => `/admin/my/agents/${agentSlug}/versions`;

export interface VersionInput {
  version?: string;
  tag?: string | null;
  soul?: string | null;
  instructions?: string | null;
  chat_instructions?: string | null;
  supports_chat?: boolean;
  skills_in_chat?: boolean;
  tools_on_demand?: boolean;
  /** null clears back to provider defaults. */
  generation_params?: GenerationParams | null;
  delivery_mode?: DeliveryMode;
  status?: AgentStatus;
  guardrail_message?: string | null;
}

export const listVersions = (agentSlug: string) => apiFetch<VersionView[]>(vBase(agentSlug));

export const getVersion = (agentSlug: string, label: string) =>
  apiFetch<VersionView>(`${vBase(agentSlug)}/${label}`);

export const createVersion = (agentSlug: string, input: VersionInput) =>
  apiFetch<VersionView>(vBase(agentSlug), { method: "POST", body: input });

export const updateVersion = (agentSlug: string, label: string, input: VersionInput) =>
  apiFetch<VersionView>(`${vBase(agentSlug)}/${label}`, { method: "PATCH", body: input });

export const publishVersion = (agentSlug: string, label: string) =>
  apiFetch<VersionView>(`${vBase(agentSlug)}/${label}/publish`, { method: "POST" });

export const unpublishVersion = (agentSlug: string, label: string) =>
  apiFetch<VersionView>(`${vBase(agentSlug)}/${label}/publish`, { method: "DELETE" });

export const deleteVersion = (agentSlug: string, label: string) =>
  apiFetch<void>(`${vBase(agentSlug)}/${label}`, { method: "DELETE" });

export const cloneVersion = (
  agentSlug: string,
  label: string,
  input: {
    version: string;
    tag?: string | null;
    /** Sent → replaces the source's params (null = provider defaults). */
    generation_params?: GenerationParams | null;
    change_note?: string | null;
  },
) => apiFetch<VersionView>(`${vBase(agentSlug)}/${label}/clone`, { method: "POST", body: input });

// ---- which version is live: compare, promote, roll back (AVI-168) -----------

const aBase = (agentSlug: string) => `/admin/my/agents/${agentSlug}`;

export const getLive = (agentSlug: string) => apiFetch<LiveVersion>(`${aBase(agentSlug)}/live`);

export const diffVersion = (agentSlug: string, label: string) =>
  apiFetch<VersionDiff>(`${vBase(agentSlug)}/${label}/diff`);

export const promoteVersion = (agentSlug: string, label: string, note: string | null) =>
  apiFetch<LiveVersion>(`${vBase(agentSlug)}/${label}/promote`, {
    method: "POST",
    body: { note },
  });

export const rollbackLive = (agentSlug: string, note: string | null) =>
  apiFetch<LiveVersion>(`${aBase(agentSlug)}/live/rollback`, { method: "POST", body: { note } });

// ---- which configured LLM model a version runs on --------------------------

export const getVersionLlm = (agentSlug: string, label: string) =>
  apiFetch<VersionLlm>(`${vBase(agentSlug)}/${label}/llm`);

/** Attach a configured model, or pass `null` to detach and inherit the tenant
 *  key. No credential crosses this boundary — the key lives on the model. */
export const setVersionLlm = (agentSlug: string, label: string, llmModelSlug: string | null) =>
  apiFetch<VersionLlm>(`${vBase(agentSlug)}/${label}/llm`, {
    method: "PUT",
    body: { llm_model_slug: llmModelSlug },
  });

export const getVersionLlmHealth = (agentSlug: string, label: string) =>
  apiFetch<VersionLlmHealth>(`${vBase(agentSlug)}/${label}/llm/health`);

// ---- Playground insights: pricing + recent runs (with cost) ---------------

export const listPricing = () => apiFetch<ModelPricing[]>("/admin/pricing");

export const listMyRuns = () => apiFetch<RunRow[]>("/admin/my/runs?limit=15");

// ---- tenant-admin authoring: skills ---------------------------------------

const sBase = (agentSlug: string, label: string) => `${vBase(agentSlug)}/${label}/skills`;

export interface SkillInput {
  name?: string;
  slug?: string;
  description?: string | null;
  instructions?: string | null;
  input_schema?: Record<string, unknown> | null;
  output_schema?: Record<string, unknown> | null;
  order?: number;
}

export const listSkills = (agentSlug: string, label: string) =>
  apiFetch<SkillView[]>(sBase(agentSlug, label));

export const createSkill = (agentSlug: string, label: string, input: SkillInput) =>
  apiFetch<SkillView>(sBase(agentSlug, label), { method: "POST", body: input });

export const updateSkill = (
  agentSlug: string,
  label: string,
  skillSlug: string,
  input: SkillInput,
) => apiFetch<SkillView>(`${sBase(agentSlug, label)}/${skillSlug}`, { method: "PUT", body: input });

export const deleteSkill = (agentSlug: string, label: string, skillSlug: string) =>
  apiFetch<void>(`${sBase(agentSlug, label)}/${skillSlug}`, { method: "DELETE" });

// ---- tenant-admin authoring: capabilities / MCP tools ---------------------

export interface CapabilityInput {
  domain_slug?: string;
  name?: string;
  tool_name?: string;
  description?: string | null;
  execution_type?: ExecutionType;
  execution_config?: Record<string, unknown>;
  risk_tier?: RiskTier;
  input_schema?: Record<string, unknown> | null;
  // Write-only MCP static-token credential. Value (re)sets it; `null` clears it;
  // omit to leave unchanged.
  secret_token?: string | null;
}

export const listCapabilities = () => apiFetch<CapabilityView[]>("/admin/my/capabilities");

export const createCapability = (input: CapabilityInput) =>
  apiFetch<CapabilityView>("/admin/my/capabilities", { method: "POST", body: input });

export const updateCapability = (toolName: string, input: CapabilityInput) =>
  apiFetch<CapabilityView>(`/admin/my/capabilities/${toolName}`, { method: "PATCH", body: input });

export const deleteCapability = (toolName: string) =>
  apiFetch<void>(`/admin/my/capabilities/${toolName}`, { method: "DELETE" });

const acBase = (agentSlug: string, label: string) =>
  `/admin/my/agents/${agentSlug}/versions/${label}/capabilities`;

export const listAttached = (agentSlug: string, label: string) =>
  apiFetch<AttachmentView[]>(acBase(agentSlug, label));

export const attachCapability = (
  agentSlug: string,
  label: string,
  input: { tool_name: string; requires_approval?: boolean },
) => apiFetch<AttachmentView>(acBase(agentSlug, label), { method: "POST", body: input });

export const updateAttachment = (
  agentSlug: string,
  label: string,
  toolName: string,
  input: { requires_approval: boolean },
) =>
  apiFetch<AttachmentView>(`${acBase(agentSlug, label)}/${toolName}`, {
    method: "PATCH",
    body: input,
  });

export const detachCapability = (agentSlug: string, label: string, toolName: string) =>
  apiFetch<void>(`${acBase(agentSlug, label)}/${toolName}`, { method: "DELETE" });

// ---- tenant-admin authoring: validators -----------------------------------

export interface ValidatorAttachInput {
  validator_slug?: string;
  order?: number;
  max_retries?: number;
  failure_behavior?: FailureBehavior;
  config?: Record<string, unknown> | null;
}

const avBase = (agentSlug: string, label: string) =>
  `/admin/my/agents/${agentSlug}/versions/${label}/validators`;

export const listValidatorCatalog = () => apiFetch<ValidatorCatalog[]>("/admin/my/validators");

export const listAttachedValidators = (agentSlug: string, label: string) =>
  apiFetch<ValidatorAttachment[]>(avBase(agentSlug, label));

export const attachValidator = (agentSlug: string, label: string, input: ValidatorAttachInput) =>
  apiFetch<ValidatorAttachment>(avBase(agentSlug, label), { method: "POST", body: input });

export const updateValidator = (
  agentSlug: string,
  label: string,
  validatorSlug: string,
  input: Omit<ValidatorAttachInput, "validator_slug">,
) =>
  apiFetch<ValidatorAttachment>(`${avBase(agentSlug, label)}/${validatorSlug}`, {
    method: "PATCH",
    body: input,
  });

export const detachValidator = (agentSlug: string, label: string, validatorSlug: string) =>
  apiFetch<void>(`${avBase(agentSlug, label)}/${validatorSlug}`, { method: "DELETE" });

// ---- tenant-admin authoring: knowledge bases ------------------------------

const KB = "/admin/my/knowledge-bases";

/** Where a document knowledge base reads from (ADR-019).
 *
 * The bucket is the **customer's**. Aviary reads `bucket/prefix`, and writes
 * only to the upload prefix when they grant it — no document is copied into
 * storage we own. */
export interface DocumentSourceInput {
  bucket: string;
  prefix?: string;
  region?: string | null;
  /** Set for a non-AWS S3-compatible store (MinIO, R2). */
  endpoint_url?: string | null;
  /** Assumed with the organisation's own ExternalId, which the server reads
   *  itself; it is never sent from here. */
  role_arn?: string | null;
  access_key_id?: string | null;
  secret_access_key?: string | null;
}

export interface KnowledgeBaseInput {
  domain_slug: string;
  name: string;
  slug?: string;
  source_type?: "sql" | "document";
  // Write-only credentials — stored encrypted, never returned.
  /** SQL sources only. */
  source_dsn?: string | null;
  /** Document sources only. */
  document_source?: DocumentSourceInput | null;
  /** Slug of a configured embedding model — no credential is sent here. */
  embedding_model_slug: string;
  /** Slug of a registered vector store — its URL and key live there. */
  vector_store_slug: string;
  vector_collection: string;
  allowed_tables?: string[] | null;
}

export const listKnowledgeBases = () => apiFetch<KnowledgeBaseView[]>(KB);

export const getKnowledgeBase = (slug: string) => apiFetch<KnowledgeBaseView>(`${KB}/${slug}`);

export const createKnowledgeBase = (input: KnowledgeBaseInput) =>
  apiFetch<KnowledgeBaseView>(KB, { method: "POST", body: input });

// Partial update. Only include fields you want to change; credentials are
// write-only (send to rotate, omit to keep). Changing the embedding model,
// vector store or allowed tables resets the KB to `draft` — it must be
// re-ingested.
export interface KnowledgeBaseUpdateInput {
  name?: string;
  /** Repointing at a different connection forces a re-ingest. */
  embedding_model_slug?: string;
  /** Moving to a different store forces a re-ingest too. */
  vector_store_slug?: string;
  allowed_tables?: string[] | null;
  source_dsn?: string;
}

export const updateKnowledgeBase = (slug: string, input: KnowledgeBaseUpdateInput) =>
  apiFetch<KnowledgeBaseView>(`${KB}/${slug}`, { method: "PATCH", body: input });

export const deleteKnowledgeBase = (slug: string) =>
  apiFetch<void>(`${KB}/${slug}`, { method: "DELETE" });

export const checkKbConnection = (source_dsn: string) =>
  apiFetch<KbCheckConnectionResult>(`${KB}/check-connection`, {
    method: "POST",
    body: { source_dsn },
  });

/** Test a document source before saving.
 *
 * Worth doing every time: the role lives in the customer's own AWS account
 * where we cannot debug it, so a failure here is far cheaper than a knowledge
 * base that ingests nothing an hour later. */
export const checkKbDocumentSource = (document_source: DocumentSourceInput) =>
  apiFetch<KbCheckConnectionResult>(`${KB}/check-connection`, {
    method: "POST",
    body: { document_source },
  });

// ---- Documents in a document knowledge base -------------------------------

export interface KnowledgeDocumentView {
  id: number;
  object_key: string;
  filename: string;
  mime: string | null;
  bytes: number;
  status: string;
  chunk_count: number;
  page_count: number | null;
  /** Why this document failed, shown inline — with 50,000 documents nobody is
   *  going log-diving for one file. */
  last_error: string | null;
  ingested_at: string | null;
}

export interface KnowledgeDocumentPage {
  documents: KnowledgeDocumentView[];
  next_cursor: string | null;
  total: number;
  /** Counts across the whole KB, not this page. */
  status_counts: Record<string, number>;
}

export const listKbDocuments = (
  slug: string,
  opts: { cursor?: string | null; status?: string | null; limit?: number } = {},
) => {
  const params = new URLSearchParams();
  if (opts.cursor) params.set("cursor", opts.cursor);
  if (opts.status) params.set("status", opts.status);
  params.set("limit", String(opts.limit ?? 50));
  return apiFetch<KnowledgeDocumentPage>(`${KB}/${slug}/documents?${params}`);
};

export interface DocumentUploadUrl {
  url: string;
  object_key: string;
  expires_in: number;
}

/** Ask for a presigned PUT. The bytes never pass through the Aviary API — the
 *  browser sends them straight to the customer's bucket. */
export const createDocumentUploadUrl = (slug: string, filename: string, content_type: string) =>
  apiFetch<DocumentUploadUrl>(`${KB}/${slug}/upload-url`, {
    method: "POST",
    body: { filename, content_type },
  });

/** PUT a file directly to object storage using a presigned URL.
 *
 * Deliberately a bare `fetch`, not `apiFetch`: this request goes to S3, not to
 * us. Sending our session cookie to a third-party origin would be wrong, and
 * the response is not our JSON envelope. */
export async function uploadToPresignedUrl(
  url: string,
  file: File,
  contentType: string,
): Promise<void> {
  const response = await fetch(url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentType },
    credentials: "omit",
  });
  if (!response.ok) {
    throw new ApiError(
      response.status,
      `Upload failed (${response.status}). The presigned URL may have expired — try again.`,
    );
  }
}

export const ingestKnowledgeBase = (slug: string) =>
  apiFetch<KnowledgeBaseView>(`${KB}/${slug}/ingest`, { method: "POST" });

export const queryKnowledgeBase = (slug: string, question: string) =>
  apiFetch<KbQueryResult>(`${KB}/${slug}/query`, { method: "POST", body: { question } });

// Attach a KB to an agent version.
const kbAttachBase = (agentSlug: string, label: string) =>
  `/admin/my/agents/${agentSlug}/versions/${label}/knowledge-bases`;

export const listAttachedKbs = (agentSlug: string, label: string) =>
  apiFetch<KbAttachmentView[]>(kbAttachBase(agentSlug, label));

export const attachKb = (agentSlug: string, label: string, slug: string) =>
  apiFetch<KbAttachmentView>(kbAttachBase(agentSlug, label), {
    method: "POST",
    body: { slug },
  });

export const detachKb = (agentSlug: string, label: string, kbSlug: string) =>
  apiFetch<void>(`${kbAttachBase(agentSlug, label)}/${kbSlug}`, { method: "DELETE" });

// ---- Agent-config governance (ADR-016) -------------------------------------
//
// The `.claude` / `.cursor` governance surface: connect a GitHub org, choose
// which repos Aviary may read, and triage what pull-request reviews found.

const GH = "/admin/my/github";
const GOV = "/admin/my/governance";

export const getGithubInstallation = () => apiFetch<GithubInstallation>(`${GH}/installation`);

/** Bind an installation to this tenant. `state` is the signed value GitHub
 *  echoed back from the install URL — it, not the session, decides which
 *  tenant the installation attaches to. `code` is GitHub's one-time OAuth code
 *  for the user who clicked Install; the API uses it to check that user can
 *  actually see the installation (AVI-146). */
export const completeGithubInstall = (installation_id: number, state: string, code: string) =>
  apiFetch<GithubInstallation>(`${GH}/installation/callback`, {
    method: "POST",
    body: { installation_id, state, code },
  });

export const disconnectGithub = () => apiFetch<void>(`${GH}/installation`, { method: "DELETE" });

/** `refresh` re-reads the repo list from GitHub, so the picker stays honest
 *  when someone changes the App's repository selection on GitHub's side. */
export const listGovernedRepos = (refresh = true) =>
  apiFetch<GovernedRepoView[]>(`${GH}/repos?refresh=${refresh}`);

export const updateGovernedRepo = (
  repoId: number,
  input: { enabled?: boolean; watch_globs?: string[] | null; checks?: string[] | null },
) => apiFetch<GovernedRepoView>(`${GH}/repos/${repoId}`, { method: "PATCH", body: input });

// ---- Repo Governance security ----------------------------------------------

export const listRepoChecks = () => apiFetch<RepoCheck[]>(`${GOV}/checks`);

/** Org admins: scan the default branch (`full`) or recent commits (`history`). */
export const startRepoScan = (repoId: number, kind: "full" | "history") =>
  apiFetch<RepoScanView>(`${GH}/repos/${repoId}/security-scan`, {
    method: "POST",
    body: { kind },
  });

export interface SecurityFindingFilters {
  repoId?: number;
  check?: string;
  severity?: GovernanceSeverity;
  status?: SecurityFindingStatus | "all";
  scope?: "repo" | "pr" | "all";
  prNumber?: number;
}

export const listSecurityFindings = (f: SecurityFindingFilters = {}) => {
  const params = new URLSearchParams();
  if (f.repoId != null) params.set("repo_id", String(f.repoId));
  if (f.check) params.set("check", f.check);
  if (f.severity) params.set("severity", f.severity);
  if (f.status) params.set("status", f.status);
  if (f.scope) params.set("scope", f.scope);
  if (f.prNumber != null) params.set("pr_number", String(f.prNumber));
  const qs = params.toString();
  return apiFetch<SecurityFindingView[]>(`${GOV}/security/findings${qs ? `?${qs}` : ""}`);
};

export const updateSecurityFinding = (
  findingId: number,
  status: Exclude<SecurityFindingStatus, "fixed">,
  note?: string,
) =>
  apiFetch<SecurityFindingView>(`${GOV}/security/findings/${findingId}`, {
    method: "PATCH",
    body: { status, note: note ?? null },
  });

export const listRepoScans = (f: { repoId?: number; kind?: ScanKind } = {}) => {
  const params = new URLSearchParams();
  if (f.repoId != null) params.set("repo_id", String(f.repoId));
  if (f.kind) params.set("kind", f.kind);
  const qs = params.toString();
  return apiFetch<RepoScanView[]>(`${GOV}/security/scans${qs ? `?${qs}` : ""}`);
};

export const getSecuritySummary = () => apiFetch<SecuritySummary>(`${GOV}/security/summary`);

export const getSecurityAiReview = () => apiFetch<SecurityAiReview>(`${GOV}/security/ai-review`);

/** Org admins: switch the AI review of findings on or off, and set its per-scan cap. */
export const setSecurityAiReview = (input: { enabled: boolean; max_findings: number }) =>
  apiFetch<SecurityAiReview>(`${GOV}/security/ai-review`, { method: "PUT", body: input });

/** Org admins: clear the AI's verdicts (never a person's triage) and queue scans
 *  that review those findings again with the current prompt and model. */
export const rerunSecurityAiReview = (githubRepoId?: number) =>
  apiFetch<{ reset: number; scans: RepoScanView[] }>(`${GOV}/security/ai-review/rerun`, {
    method: "POST",
    body: githubRepoId == null ? {} : { github_repo_id: githubRepoId },
  });

export const syncGovernedRepo = (repoId: number) =>
  apiFetch<GovernedRepoView>(`${GH}/repos/${repoId}/sync`, { method: "POST" });

export const listGovernedFiles = (repoId: number) =>
  apiFetch<GovernedFileView[]>(`${GH}/repos/${repoId}/files`);

export interface ReviewFilters {
  repoId?: number;
  verdict?: GovernanceVerdict;
  status?: ReviewStatus;
}

export const listPrReviews = (filters: ReviewFilters = {}) => {
  const params = new URLSearchParams();
  if (filters.repoId != null) params.set("repo_id", String(filters.repoId));
  if (filters.verdict) params.set("verdict", filters.verdict);
  if (filters.status) params.set("status", filters.status);
  const qs = params.toString();
  return apiFetch<PrReviewView[]>(`${GOV}/reviews${qs ? `?${qs}` : ""}`);
};

export const getPrReview = (reviewId: number) =>
  apiFetch<PrReviewDetail>(`${GOV}/reviews/${reviewId}`);

export const updateFinding = (findingId: number, status: FindingStatus, note?: string) =>
  apiFetch<GovernanceFindingView>(`${GOV}/findings/${findingId}`, {
    method: "PATCH",
    body: { status, note: note ?? null },
  });

export const listGovernanceActivity = (filters: { repoId?: number; actor?: string } = {}) => {
  const params = new URLSearchParams();
  if (filters.repoId != null) params.set("repo_id", String(filters.repoId));
  if (filters.actor) params.set("actor", filters.actor);
  const qs = params.toString();
  return apiFetch<GovernanceActivityView[]>(`${GOV}/activity${qs ? `?${qs}` : ""}`);
};

export const getGovernanceSummary = () => apiFetch<GovernanceSummary>(`${GOV}/summary`);

export const getGovernanceSettings = () => apiFetch<GovernanceSettings>(`${GOV}/settings`);

/** Choose the model the risk analyzer runs on; `null` runs the rule pack only. */
export const setGovernanceSettings = (llmModelSlug: string | null) =>
  apiFetch<GovernanceSettings>(`${GOV}/settings`, {
    method: "PUT",
    body: { llm_model_slug: llmModelSlug },
  });

// ---- Model registry --------------------------------------------------------

const LLM_MODELS = "/admin/my/llm-models";
const EMBEDDING_MODELS = "/admin/my/embedding-models";

export const listLlmModels = () => apiFetch<LlmModelView[]>(LLM_MODELS);

export const createLlmModel = (input: ModelInput) =>
  apiFetch<LlmModelView>(LLM_MODELS, { method: "POST", body: input });

export const deleteLlmModel = (slug: string) =>
  apiFetch<void>(`${LLM_MODELS}/${slug}`, { method: "DELETE" });

/** One live call against the provider; records the outcome on the row. */
export const testLlmModel = (slug: string) =>
  apiFetch<ModelTestResult>(`${LLM_MODELS}/${slug}/test`, { method: "POST" });

export const listEmbeddingModels = () => apiFetch<EmbeddingModelView[]>(EMBEDDING_MODELS);

export const createEmbeddingModel = (input: ModelInput) =>
  apiFetch<EmbeddingModelView>(EMBEDDING_MODELS, { method: "POST", body: input });

export const deleteEmbeddingModel = (slug: string) =>
  apiFetch<void>(`${EMBEDDING_MODELS}/${slug}`, { method: "DELETE" });

export const testEmbeddingModel = (slug: string) =>
  apiFetch<ModelTestResult>(`${EMBEDDING_MODELS}/${slug}/test`, { method: "POST" });

// ---- Vector stores ---------------------------------------------------------

const VECTOR_STORES = "/admin/my/vector-stores";

export const listVectorStores = () => apiFetch<VectorStoreView[]>(VECTOR_STORES);

export const createVectorStore = (input: VectorStoreInput) =>
  apiFetch<VectorStoreView>(VECTOR_STORES, { method: "POST", body: input });

/** The URL is refused (409) while knowledge bases keep vectors in the store. */
export const updateVectorStore = (slug: string, input: VectorStoreUpdateInput) =>
  apiFetch<VectorStoreView>(`${VECTOR_STORES}/${slug}`, { method: "PATCH", body: input });

export const deleteVectorStore = (slug: string) =>
  apiFetch<void>(`${VECTOR_STORES}/${slug}`, { method: "DELETE" });

/** Connects and lists collections; records the outcome on the row. */
export const testVectorStore = (slug: string) =>
  apiFetch<ModelTestResult>(`${VECTOR_STORES}/${slug}/test`, { method: "POST" });

/** Known models per provider, served from the runtime's own registry so the
 *  picker can't drift from what the backend can actually route. */
export const getModelCatalog = () => apiFetch<ModelCatalog>("/admin/my/model-catalog");

// ---- superadmin: platform model catalogue ----------------------------------

const platformModels = (kind: PlatformModelKind) => `/admin/platform-models/${kind}`;
const platformModel = (kind: PlatformModelKind, modelId: string) =>
  `${platformModels(kind)}/${encodeURIComponent(modelId)}`;

export const listPlatformLlmModels = () => apiFetch<PlatformLlmModel[]>(platformModels("llm"));

export const listPlatformEmbeddingModels = () =>
  apiFetch<PlatformEmbeddingModel[]>(platformModels("embedding"));

export const createPlatformLlmModel = (input: PlatformLlmModelInput) =>
  apiFetch<PlatformLlmModel>(platformModels("llm"), { method: "POST", body: input });

export const updatePlatformLlmModel = (modelId: string, input: PlatformLlmModelUpdate) =>
  apiFetch<PlatformLlmModel>(platformModel("llm", modelId), { method: "PATCH", body: input });

export const createPlatformEmbeddingModel = (input: PlatformEmbeddingModelInput) =>
  apiFetch<PlatformEmbeddingModel>(platformModels("embedding"), { method: "POST", body: input });

export const updatePlatformEmbeddingModel = (
  modelId: string,
  input: PlatformEmbeddingModelUpdate,
) =>
  apiFetch<PlatformEmbeddingModel>(platformModel("embedding", modelId), {
    method: "PATCH",
    body: input,
  });

/** Hide the model from tenant pickers and refuse runs on it. `reason` is shown
 *  to tenants in the refusal. */
export const discardPlatformModel = (kind: PlatformModelKind, modelId: string, reason: string) =>
  apiFetch<unknown>(`${platformModel(kind, modelId)}/discard`, {
    method: "POST",
    body: { reason: reason.trim() || null },
  });

export const restorePlatformModel = (kind: PlatformModelKind, modelId: string) =>
  apiFetch<unknown>(`${platformModel(kind, modelId)}/restore`, { method: "POST" });
// ---- Audit log -------------------------------------------------------------

export interface AuditFilters {
  agent_slug?: string;
  run_kind?: string;
  outcome?: string;
  source?: string;
  limit?: number;
  offset?: number;
}

export const listAuditRuns = (f: AuditFilters = {}) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== "") q.set(k, String(v));
  }
  const qs = q.toString();
  return apiFetch<AuditRunPage>(`/admin/my/audit/runs${qs ? `?${qs}` : ""}`);
};

export const getAuditRun = (runId: string) =>
  apiFetch<AuditRunDetail>(`/admin/my/audit/runs/${runId}`);

const query = (f: object) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  const qs = q.toString();
  return qs ? `?${qs}` : "";
};

export interface EmbeddingAuditFilters {
  source?: string;
  status?: string;
  agent_slug?: string;
  kb_slug?: string;
  limit?: number;
  offset?: number;
}

/** Every embeddings request the tenant paid for, with totals for the filter. */
export const listAuditEmbeddings = (f: EmbeddingAuditFilters = {}) =>
  apiFetch<EmbeddingUsagePage>(`/admin/my/audit/embeddings${query(f)}`);

/** Completions made outside an agent's loop — SQL knowledge bases writing
 *  queries, from runs and from the console. Same filters as embeddings. */
export const listAuditToolLlm = (f: EmbeddingAuditFilters = {}) =>
  apiFetch<LlmUsagePage>(`/admin/my/audit/tool-llm${query(f)}`);

export interface GovernanceAuditFilters {
  verdict?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

/** Agent-config PR reviews with what the analyzer model cost. */
export const listAuditGovernance = (f: GovernanceAuditFilters = {}) =>
  apiFetch<GovernanceAuditPage>(`/admin/my/audit/governance${query(f)}`);

// ---- Semantic cache (ADR-022) -----------------------------------------------

const semanticCache = (agentSlug: string) => `/admin/my/agents/${agentSlug}/semantic-cache`;

export const getSemanticCache = (agentSlug: string) =>
  apiFetch<SemanticCacheSettings>(semanticCache(agentSlug));

export const updateSemanticCache = (agentSlug: string, input: SemanticCacheUpdate) =>
  apiFetch<SemanticCacheSettings>(semanticCache(agentSlug), { method: "PUT", body: input });

/** Drop every cached answer for the agent (all versions). */
export const clearSemanticCache = (agentSlug: string) =>
  apiFetch<void>(`${semanticCache(agentSlug)}/entries`, { method: "DELETE" });

/** The tenant's whole configuration as nodes and edges. */
export const getTenantMap = () => apiFetch<TenantMap>("/admin/my/tenant-map");

// ---- org admin: users -------------------------------------------------------

export const listMembers = () => apiFetch<Member[]>("/admin/my/users");

export const createMember = (email: string, role: MemberRole) =>
  apiFetch<MemberCredential>("/admin/my/users", { method: "POST", body: { email, role } });

export const updateMember = (id: number, input: { role?: MemberRole; is_active?: boolean }) =>
  apiFetch<Member>(`/admin/my/users/${id}`, { method: "PATCH", body: input });

export const resetMemberPassword = (id: number) =>
  apiFetch<MemberCredential>(`/admin/my/users/${id}/reset-password`, { method: "POST" });

export const deleteMember = (id: number) =>
  apiFetch<void>(`/admin/my/users/${id}`, { method: "DELETE" });

// ---- org admin: observability (LangSmith) ------------------------------------------

export const getObservability = () => apiFetch<ObservabilitySettings>("/admin/my/observability");

export const putObservability = (input: {
  enabled: boolean;
  project: string;
  endpoint?: string;
  /** Omitted = keep the stored key. */
  api_key?: string;
}) => apiFetch<ObservabilitySettings>("/admin/my/observability", { method: "PUT", body: input });

export const deleteObservability = () =>
  apiFetch<void>("/admin/my/observability", { method: "DELETE" });

export const testObservability = () =>
  apiFetch<ObservabilityTestResult>("/admin/my/observability/test", { method: "POST" });

// ---- org admin: agent behaviour (code of conduct) ------------------------------

export const getAgentBehaviour = () => apiFetch<AgentBehaviour>("/admin/my/agent-behaviour");

export const putAgentBehaviour = (input: AgentBehaviourPrinciples & { token_cap: number }) =>
  apiFetch<AgentBehaviour>("/admin/my/agent-behaviour", { method: "PUT", body: input });

export const listAgentBehaviourVersions = () =>
  apiFetch<AgentBehaviourVersion[]>("/admin/my/agent-behaviour/versions");
